import React from 'react';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import haversine from 'haversine';

import BackButton from './BackButton';
import TabMenu from '../tab-menu/TabMenu';
import { formatTerminalLocatorMileage } from '../../terminal-locator/terminalLocatorConstants';

const stickyContainerStyles = {
  backgroundColor: 'surface.white',
  position: 'sticky',
  top: '48px',
  zIndex: 3,
};

const headerContainerStyles = {
  backgroundColor: 'surface.medium',
  display: 'flex',
  flexDirection: { lg: 'row', xs: 'column' },
  pb: { lg: 4, xs: 0 },
  pt: { sm: 4, xs: 2 },
  px: { lg: 6.5, xs: 0 },
};

const titleContainerStyles = {
  mr: 8,
  pb: 2,
  pl: { lg: 0, xs: 2 },
  width: { lg: '350px', xs: '100%' },
};

const Header = ({
  alertCount,
  handleAlertClick,
  handleOverviewClick,
  isFetching,
  selectedTab,
  terminalLat,
  terminalLong,
  terminalName,
}) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });

  const [searchParams] = useSearchParams();

  const userLocation = {
    latitude: decodeURIComponent(searchParams.get('latitude')),
    longitude: decodeURIComponent(searchParams.get('longitude')),
  };

  const distance = haversine(
    userLocation,
    { latitude: terminalLat, longitude: terminalLong },
    { unit: 'mile' }
  );

  const displayDistance = formatTerminalLocatorMileage(distance);

  return (
    <Stack sx={stickyContainerStyles}>
      <Box sx={headerContainerStyles}>
        <Stack spacing={0.5} sx={titleContainerStyles}>
          <BackButton />
          {isFetching ? (
            <>
              <Skeleton height="24px" variant="rectangular" width="240px" />
              <Skeleton height="14px" variant="rectangular" width="48px" />
            </>
          ) : (
            <>
              <Typography color="text.primary" variant="h5">
                {terminalName}
              </Typography>
              <Typography variant="body2">{displayDistance}</Typography>
            </>
          )}
        </Stack>
      </Box>
      {!isMobile && (
        <>
          <TabMenu
            alertCount={alertCount}
            handleAlertClick={handleAlertClick}
            handleOverviewClick={handleOverviewClick}
            value={selectedTab}
          />
          <Divider />
        </>
      )}
    </Stack>
  );
};

Header.defaultProps = {
  alertCount: 0,
  terminalLat: null,
  terminalLong: null,
  terminalName: '--',
};

Header.propTypes = {
  alertCount: PropTypes.number,
  handleAlertClick: PropTypes.func.isRequired,
  handleOverviewClick: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  selectedTab: PropTypes.string.isRequired,
  terminalLat: PropTypes.number,
  terminalLong: PropTypes.number,
  terminalName: PropTypes.string,
};

export default Header;
